import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';

import { CheckinBlockerSessionDetails, CheckinBlockerUser } from './_types';

type SessionDetailSectionProps = {
  label: string;
  value: string;
  secondaryValues?: string[];
};

function SessionDetailSection({
  label,
  value,
  secondaryValues = [],
}: SessionDetailSectionProps) {
  return (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      <Typography variant="bodyLargeStrong">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
      {secondaryValues.map((secondaryValue, index) => (
        <Typography
          key={`${index}-${secondaryValue}`}
          variant="body2"
          color="text.secondary"
        >
          {secondaryValue}
        </Typography>
      ))}
    </Stack>
  );
}

type BookingSessionDetailsProps = {
  sessionDetails: CheckinBlockerSessionDetails;
  bookForUser?: CheckinBlockerUser;
};

/**
 * Right-column booking metadata: Book for, staff, location, when, who's going.
 */
export function BookingSessionDetails({
  sessionDetails,
  bookForUser,
}: BookingSessionDetailsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'checkinBlocker.sessionDetails',
  });

  const whenValue = (() => {
    const formattedStart = formatFullDateAndTime(sessionDetails.startTime, {
      weekday: 'long',
      year: undefined,
    });

    if (sessionDetails.duration == null) {
      return formattedStart;
    }

    return t('whenWithDuration', {
      when: formattedStart,
      duration: sessionDetails.duration,
    });
  })();

  const locationSecondaryValues = [
    sessionDetails.locationAddress,
    sessionDetails.locationInstructions,
    ...(sessionDetails.resourceInstructions
      ? sessionDetails.resourceInstructions.split('\n').filter(Boolean)
      : []),
  ].filter((value): value is string => !!value?.trim());

  const bookForName = bookForUser?.name?.trim();

  return (
    <Stack spacing={2.5} sx={{ width: '100%' }}>
      {bookForName ? (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            alt={bookForName}
            size="small"
            src={bookForUser?.profileImage}
          />
          <Typography variant="body2">
            {t('bookFor', { name: bookForName })}
          </Typography>
        </Stack>
      ) : null}

      {sessionDetails.trainerNames ? (
        <SessionDetailSection
          label={t('staff')}
          value={sessionDetails.trainerNames}
        />
      ) : null}
      {sessionDetails.locationName ? (
        <SessionDetailSection
          label={t('location')}
          value={sessionDetails.locationName}
          secondaryValues={locationSecondaryValues}
        />
      ) : null}
      <SessionDetailSection label={t('when')} value={whenValue} />
      {sessionDetails.attendeeCount != null ? (
        <SessionDetailSection
          label={t('whosGoing')}
          value={t('attendeeCount', { count: sessionDetails.attendeeCount })}
        />
      ) : null}
    </Stack>
  );
}
